import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@ui/atoms/Button';
import { Table, TableSize } from '@ui/molecules/Table';

import { t } from '../constants';

// IP-15330: the per-type usage breakdown inside the tag Brushaway.
//
// Quimby Table, `detail` variant (Storybook: Molecules/Table/DetailWithSelection)
// — the same component the listing uses, rather than the hand-written
// `table.qmb-table.w-50` this replaced.
//
// Only types with at least one record are listed, ordered by count, because the
// question this answers is "where does this tag actually live".
function RemoveCell({ item }) {
  return (
    <Button
      title={item.removeLabel}
      aria-label={item.removeLabel}
      disabled={item.disabled}
      onClick={item.onRemove}
      data-taggable-type={item.taggableType}>
      <i className="fa-light fa-link-slash" aria-hidden="true" />
    </Button>
  );
}

RemoveCell.propTypes = {
  item: PropTypes.shape({
    taggableType: PropTypes.string.isRequired,
    removeLabel: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    onRemove: PropTypes.func.isRequired
  }).isRequired
};

function TagUsageTable({ countsByType, taggableTypeLabels, onRemove, disabled }) {
  // Tolerates a missing map rather than trusting the payload. Object.entries(undefined)
  // throws, and there is no error boundary above this: a malformed response took the
  // whole listing down with it, not just the drawer.
  const rows = useMemo(
    () =>
      Object.entries(countsByType || {})
        .filter(([, count]) => count > 0)
        .sort(([, a], [, b]) => b - a)
        .map(([taggableType, count]) => ({
          id: taggableType,
          taggableType,
          typeLabel: taggableTypeLabels[taggableType] || taggableType,
          count,
          disabled,
          removeLabel: t('drawer.usage.remove'),
          onRemove: () => onRemove(taggableType)
        })),
    [countsByType, taggableTypeLabels, onRemove, disabled]
  );

  const columns = useMemo(
    () => [
      { dataKey: 'typeLabel', label: t('drawer.usage.type'), columnType: 'primary' },
      { dataKey: 'count', label: t('drawer.usage.records'), width: '110px' },
      { dataKey: 'remove', label: '', width: '64px', columnType: 'action', renderer: RemoveCell }
    ],
    []
  );

  return (
    <Table
      data={rows}
      columns={columns}
      variant="detail"
      size={TableSize.Full}
      getItemId={(item) => item.id}
      emptyContent={t('drawer.usage.unused')}
    />
  );
}

TagUsageTable.propTypes = {
  countsByType: PropTypes.objectOf(PropTypes.number),
  taggableTypeLabels: PropTypes.objectOf(PropTypes.string).isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool
};

TagUsageTable.defaultProps = {
  countsByType: {},
  disabled: false
};

export default TagUsageTable;
